import { useEffect, useState } from "react";

import {
  faEdit,
  faFlask,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { useWorkspace } from "src/api/queries";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Skeleton } from "src/design-system/Skeleton";
import { EntitiesTable } from "src/entities/EntitiesTable";
import { getSchemaIcon } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useEnvironment } from "src/hooks/useEnvironment";
import { PageHeader } from "src/layout/PageHeader";
import {
  SubHeader,
  SubHeaderSearchBox,
  SubHeaderTab,
  SubHeaderTabs,
} from "src/layout/SubHeader";
import { ErrorPage } from "src/router/ErrorPage";
import { EntitiesParams, getUrlToSchemaEntitiesPage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const LoadingTabs = () => (
  <div className="flex h-full items-center gap-2">
    {Array(7)
      .fill(null)
      .map((_, index) => (
        <Skeleton key={index} height="h-2/3" width="w-15" />
      ))}
  </div>
);

export const Entities = () => {
  const [environment, setEnvironment] = useEnvironment();
  const { wsId, orgId, schema } = useParamsDecode<EntitiesParams>();
  const workspace = useWorkspace(wsId);
  const { data, isLoading } = useEntitySchemas({
    baseUrl: workspace.data?.base_url ?? "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const foundSchema = data?.entities.find((entity) => entity._id === schema);

  useDocumentTitle(
    foundSchema ? [foundSchema._display_name_plural, "Entities"] : ["Entities"],
  );

  const switchEnvironmentDropdownItem: OptionsDropdownElement = {
    key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
    icon: environment === "sandbox" ? faWavePulse : faFlask,
    action: () =>
      setEnvironment(environment === "sandbox" ? "live" : "sandbox"),
  };

  useEffect(() => {
    if (!schema && data?.entities.length) {
      navigate(getUrlToSchemaEntitiesPage(orgId, wsId, data.entities[0]._id), {
        replace: true,
      });
    }
  }, [data?.entities, schema, orgId, wsId, navigate]);

  if (schema && !foundSchema && !isLoading) {
    return <ErrorPage message="404: Page not found" />;
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Entities"
        sideContent={
          <span className="h-full w-[49px] border-gray-200 border-l p-3.5">
            <EllipsisOptionsDropdown
              elements={[switchEnvironmentDropdownItem]}
              iconSize="sm"
              placement="bottom start"
            />
          </span>
        }
      />
      <SubHeader
        leftSlot={
          <SubHeaderTabs dataLoc="entities-tabs">
            {isLoading ? (
              <LoadingTabs />
            ) : (
              data?.entities.map((schema) => (
                <SubHeaderTab
                  key={schema._id}
                  icon={getSchemaIcon(schema)}
                  to={getUrlToSchemaEntitiesPage(orgId, wsId, schema._id)}
                  rightSlot={
                    <EllipsisOptionsDropdown
                      elements={[
                        {
                          key: "Edit Schema",
                          icon: faEdit,
                          action: () => {
                            navigate(
                              getUrlToSchemaEntitiesPage(
                                orgId,
                                wsId,
                                schema._id,
                                { keepFlags: true, schemaEditor: true },
                              ),
                            );
                          },
                        },
                      ]}
                      iconSize="xs"
                      placement="bottom start"
                    />
                  }
                >
                  <p className="pr-1 capitalize">
                    {schema._display_name_plural}
                  </p>
                </SubHeaderTab>
              ))
            )}
          </SubHeaderTabs>
        }
      >
        <SubHeaderSearchBox
          dataLoc="entities-search-box"
          placeholder="Search by ID"
          onChange={setSearchQuery}
        />
      </SubHeader>
      {foundSchema && (
        <EntitiesTable
          key={foundSchema._id}
          entitySchema={foundSchema}
          environment={environment}
          schemasLoading={isLoading}
          searchQuery={searchQuery}
        />
      )}
    </div>
  );
};
